import type {
  ExtractedFileData,
  RiskAnalysisTransformResult,
  RiskAnalysisSectorData,
  ParsedFileData,
} from "../types";
import {
  RISK_ANALYSIS_SECTOR_SHEETS,
  RISK_ANALYSIS_ACTOR_NAMES,
  RISK_ANALYSIS_ATTACK_CATEGORIES,
} from "../constants";
import { getCellValue, findSheetByNameOrIndex } from "../parsers";

export function transformRiskAnalysis(
  extracted: ExtractedFileData,
  parsedFile?: ParsedFileData
): RiskAnalysisTransformResult {
  const val = (fieldName: string) =>
    extracted.fields[fieldName]?.parsedValue ?? null;
  const str = (fieldName: string) => val(fieldName) as string | null;

  const selectedSectors: string[] = [];
  for (const sector of RISK_ANALYSIS_SECTOR_SHEETS) {
    const fieldName = `sector_${sector.sectorId}`;
    const fieldVal = str(fieldName);
    if (fieldVal && fieldVal.toLowerCase() === "da") {
      selectedSectors.push(sector.sectorId);
    }
  }

  const sectorResults: RiskAnalysisSectorData[] = [];
  if (parsedFile) {
    for (const sectorDef of RISK_ANALYSIS_SECTOR_SHEETS) {
      if (!selectedSectors.includes(sectorDef.sectorId)) continue;
      const sectorData = extractSectorMatrix(
        parsedFile,
        sectorDef.sheetName,
        sectorDef.sectorId,
        sectorDef.sectorName
      );
      if (sectorData) sectorResults.push(sectorData);
    }
  }

  return {
    entityData: {
      denumire: str("denumire_entitate") ?? undefined,
      cui: str("cui") ?? undefined,
      judet: str("judet") ?? undefined,
      localitate: str("localitate") ?? undefined,
      sector: str("sector") ?? undefined,
      dimensiune: str("dimensiune") ?? undefined,
      reprezentantLegal: str("reprezentant_legal") ?? undefined,
      functia: str("functia") ?? undefined,
      data: str("data") ?? undefined,
    },
    selectedSectors,
    sectorResults,
  };
}

function extractSectorMatrix(
  parsedFile: ParsedFileData,
  sheetName: string,
  sectorId: string,
  sectorName: string
): RiskAnalysisSectorData | null {
  const sheet = findSheetByNameOrIndex(parsedFile, sheetName);
  if (!sheet) return null;

  const dimensionScore = Number(getCellValue(sheet, "C7") ?? 0);

  // Attack rows are 9-13 (0-indexed: rows 8-12)
  // Columns: B=attack name, C=nature, D=impact, E/G/I/K/M=probability, F/H/J/L/N=risk value
  const probabilityCols = ["E", "G", "I", "K", "M"];
  const riskValueCols = ["F", "H", "J", "L", "N"];

  const attackCategories = RISK_ANALYSIS_ATTACK_CATEGORIES.map(
    (attackName, idx) => {
      const row = 9 + idx; // rows 9-13
      const attackNature = Number(getCellValue(sheet, `C${row}`) ?? 0);
      const impact = String(getCellValue(sheet, `D${row}`) ?? "");

      const actors = RISK_ANALYSIS_ACTOR_NAMES.map((actorName, actorIdx) => ({
        name: actorName,
        probability: String(
          getCellValue(sheet, `${probabilityCols[actorIdx]}${row}`) ?? ""
        ),
        riskValue: Number(
          getCellValue(sheet, `${riskValueCols[actorIdx]}${row}`) ?? 0
        ),
      }));

      return { name: attackName, attackNature, impact, actors };
    }
  );

  const totalScore = Number(getCellValue(sheet, "O14") ?? 0);
  const requirementCategory = String(getCellValue(sheet, "P14") ?? "de BAZĂ");

  return {
    sectorName,
    sectorId,
    dimensionScore,
    attackCategories,
    totalScore,
    requirementCategory,
  };
}
